// Adapted from the Institute's existing public programme descriptions.
// The historical Feb–Jun 2026 schedule is not a current enrollment calendar.
export const programmeFilters = [
  "All programmes",
  "Chanting & meditation",
  "Philosophy & texts",
  "Living traditions",
] as const;
export type ProgrammeFilter = (typeof programmeFilters)[number];
export type Programme = {
  id: string;
  title: string;
  sanskrit: string;
  category: Exclude<ProgrammeFilter, "All programmes">;
  kind: string;
  description: string;
  detail: string;
  topics: string[];
  tone: "rose" | "sage" | "sand";
  icon: "chant" | "consciousness" | "leaf" | "book" | "text" | "sky";
};
export const programmes: Programme[] = [
  {
    id: "lalita",
    title: "Lalitā Sahasranāma",
    sanskrit: "श्रीललितासहस्रनाम",
    category: "Chanting & meditation",
    kind: "Chanting & study circle",
    tone: "rose",
    icon: "chant",
    description:
      "Learn to chant with care. Discover the meaning within each name, and carry it into daily contemplation.",
    detail:
      "A gentle, guided immersion in chanting, pronunciation and meaning. Study circles explore cosmology, compassion and mantra śāstra, supported by transliteration material, commentary and reflection.",
    topics: [
      "Careful pronunciation and contemplative chanting",
      "Meaning circles and selected commentary",
      "Breathing, meditation and reflective journaling",
    ],
  },
  {
    id: "shaivism",
    title: "Kashmiri Śaivism",
    sanskrit: "चितिः स्वतन्त्रा",
    category: "Philosophy & texts",
    kind: "Human consciousness study group",
    tone: "sage",
    icon: "consciousness",
    description:
      "Explore consciousness, recognition and the living experience of awareness through the texts of Trika.",
    detail:
      "A seminar-style study of Pratyabhijñāhṛdayam and Spanda Kārikā. Clear translations, source texts and discussion meet short contemplative practices, bringing philosophical understanding into lived enquiry.",
    topics: [
      "Recognition, grace and the tattvas",
      "Pratyabhijñāhṛdayam and Spanda Kārikā",
      "Guided dhāraṇā, contemplative enquiry and peer discussion",
    ],
  },
  {
    id: "ayurveda",
    title: "Āyurveda Classes",
    sanskrit: "आयुर्वेदः",
    category: "Living traditions",
    kind: "Classes & practical workshops",
    tone: "sand",
    icon: "leaf",
    description:
      "Study the principles of Āyurveda and their relationship to daily routines, balance and wellbeing.",
    detail:
      "An introduction to Āyurveda as an upaveda, combining the study of traditional principles with discussion of everyday practices and approaches to wellbeing.",
    topics: [
      "Āyurveda within the Vedic knowledge tradition",
      "Daily practices and routines",
      "Traditional approaches to wellbeing",
    ],
  },
  {
    id: "upanishads",
    title: "Upaniṣad Study Circle",
    sanskrit: "उपनिषदः",
    category: "Philosophy & texts",
    kind: "Close reading & contemplation",
    tone: "sand",
    icon: "book",
    description:
      "Read slowly, ask freely. Explore selected Upaniṣads through word-by-word study and shared reflection.",
    detail:
      "Each term centres on selected Upaniṣads, with time for careful reading, word-by-word understanding, philosophical dialogue and reflection on everyday life. Text selections are announced for each term.",
    topics: [
      "Selected source texts and word-by-word study",
      "Translation and philosophical dialogue",
      "Contemplation and everyday application",
    ],
  },
  {
    id: "vedas-tantras",
    title: "Vedas & Tantras",
    sanskrit: "वेदाः तन्त्राणि च",
    category: "Philosophy & texts",
    kind: "Study group & research seminars",
    tone: "rose",
    icon: "text",
    description:
      "Engage with Vedic and Tantric texts through seminars, research conversations and contemplative study.",
    detail:
      "A space for sustained engagement with Vedic and Tantric traditions. Programmes bring together textual seminars, research and thesis support, and meditative practice.",
    topics: [
      "Text-centred seminars and discussion",
      "Research and thesis support",
      "Contemplative engagement with the tradition",
    ],
  },
  {
    id: "jyotisha",
    title: "Jyotiṣa Cosmology",
    sanskrit: "ज्योतिषम्",
    category: "Living traditions",
    kind: "Astronomy & cosmology lab",
    tone: "sage",
    icon: "sky",
    description:
      "Study the sky, its patterns and cycles, and the astronomical foundations of the Jyotiṣa tradition.",
    detail:
      "Rotating workshops explore rāśi geometry, nakṣatra astronomy and time cycles, with observation, chart study and discussion of the historical development of Jyotiṣa.",
    topics: [
      "Rāśi geometry and nakṣatra astronomy",
      "Time cycles and observation",
      "Historical context and thoughtful interpretation",
    ],
  },
];
